import { useEffect, useState } from 'react';
import type { UserProfile } from '@/lib/auth/client';
import { useAuth } from '@/lib/auth/react';
import { useAppUser } from '@/lib/useAppUser';
import { needsEmailVerification } from '@/lib/onboarding';
import { useOpenLogin, useOpenVerifyEmail, useCloseAuthModal } from '@/store/authModalStore';
import { useStartSignup, useFinishSignup } from '@/store/signupProgressStore';
import { AppLogo } from '@/components/icons/AppLogo';
import type { SignupStep } from './SignupStepper';
import { SignupStepMethod } from './SignupStepMethod';
import { SignupStepTerms } from './SignupStepTerms';
import { SheetBackButton } from './auth-form-ui';

const TEST_ID_PREFIX = 'auth-signup-sheet';

/**
 * Pick the step the sheet should show for the given profile. The sheet only
 * owns `method` (pre-signup) and `terms` (post-verification); the intermediate
 * email-verification stage is handled by the full-screen {@link EmailVerificationPage},
 * not here. A user who still needs to verify is redirected off the sheet by the
 * hand-off effect below.
 */
function initialSignupStep(user: UserProfile | null): SignupStep {
  return user && !needsEmailVerification(user) ? 'terms' : 'method';
}

/**
 * Multi-step signup flow rendered inside a bottom sheet.
 *
 * Owns the current step. The starting step is computed from the live
 * `useAppUser()` profile so a re-entered funnel resumes in the right place: no
 * session → `method`, a verified session → `terms`. A user who still needs to
 * verify their email never stays here — the sheet hands the modal over to the
 * app-level {@link EmailVerificationPage} (mode `verifyEmail`) until the email
 * is verified, then the funnel resumes at `terms`.
 *
 * Step sequence: method → emailVerified → terms. Address and phone are no longer
 * collected here; consent recorded at the terms step is the only server write
 * the funnel performs (see `SignupStepTerms`).
 */
export interface SignupSheetProps {
  /** Reports the active step up to the owner so the dismiss handler (which lives
   *  on the parent's BottomSheet) can attribute an intentional exit to the right
   *  phase. */
  onStepChange?: (step: SignupStep) => void;
  /** Back chevron action — same semantics as a sheet dismiss. */
  onBack?: () => void;
}

export function SignupSheet({ onStepChange, onBack }: SignupSheetProps = {}) {
  const openLogin = useOpenLogin();
  const openVerifyEmail = useOpenVerifyEmail();
  const closeAuthModal = useCloseAuthModal();

  const { client } = useAuth();
  const user = useAppUser();
  const startSignup = useStartSignup();
  const finishSignup = useFinishSignup();

  // Mark a signup as underway so it survives a refresh: the modal store is
  // in-memory, but this persisted flag lets a re-entry resume the funnel at the
  // user's next step, keeps the TermsGate suppressed, and holds the app shell on
  // the lander until signup completes.
  useEffect(() => {
    startSignup();
  }, [startSignup]);

  const [step, setStep] = useState<SignupStep>(() => initialSignupStep(user));

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Hand-off: a session that still needs email verification never sits on the
  // sheet — the verification step owns the screen. Covers both the mid-flow
  // transition (email signup just resolved) and a funnel re-entry after a
  // refresh, where Firebase restores the unverified session asynchronously.
  useEffect(() => {
    if (step === 'method' && needsEmailVerification(user)) openVerifyEmail();
  }, [step, user, openVerifyEmail]);

  return (
    <div data-testid={TEST_ID_PREFIX} className="relative flex flex-col gap-10 w-full max-w-[377px] mx-auto min-h-[640px]">
      {onBack && <SheetBackButton testId={`${TEST_ID_PREFIX}-back-button`} onClick={onBack} />}
      <div className="flex flex-col items-center">
        <AppLogo width={248} disableAnimation={true} />
      </div>

      <div className="flex-1 flex flex-col justify-start">
        {step === 'method' && (
          <SignupStepMethod
            onMethodComplete={(_email, method) => {
              // Continue-as-guest is an exit, not a step: no account exists, so
              // there are no terms to accept — close the funnel and clear the
              // in-progress flag so nothing tries to resume it.
              if (method === 'guest') {
                finishSignup();
                closeAuthModal();
                return;
              }
              // `client.getUser()` is updated synchronously by AuthClient before
              // signUp/signInWithGoogle resolves, so we read the just-returned
              // profile here. Email/password signups must verify first: switch the
              // modal to the verification step (EmailVerificationPage renders it).
              // Social logins arrive verified and go straight to the terms step.
              if (needsEmailVerification(client.getUser())) {
                openVerifyEmail();
              } else {
                setStep('terms');
              }
            }}
            onSwitchToLogin={openLogin}
          />
        )}

        {step === 'terms' && (
          <SignupStepTerms
            onAccept={() => {
              // TODO: persist `marketingOptIn` via the generated users hook once
              // the api exposes the endpoint. Document consent is already
              // recorded inside the terms step.
              closeAuthModal();
            }}
          />
        )}
      </div>
    </div>
  );
}
